import { TAGS } from '@/lib/analytics/config'
import { allows, type Consent } from '@/lib/analytics/consent'

interface HotjarStub {
  (...args: unknown[]): void
  q?: unknown[][]
}

interface LintrkStub {
  (...args: unknown[]): void
  q?: unknown[][]
}

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
    fbq?: ((...args: unknown[]) => void) & { queue?: unknown[][] }
    _fbq?: unknown
    hj?: HotjarStub
    _hjSettings?: { hjid: number; hjsv: number }
    lintrk?: LintrkStub
    _linkedin_partner_id?: string
    _linkedin_data_partner_ids?: string[]
    hsConversationsSettings?: { loadImmediately: boolean }
  }
}

const loaded = new Set<string>()
/** GA reports only while this is on: a Decline after the default load turns it off. */
let gaOn = false

function inject(src: string, attrs: Record<string, string> = {}) {
  if (document.querySelector(`script[src="${src}"]`)) return
  const s = document.createElement('script')
  s.src = src
  s.async = true
  for (const [k, v] of Object.entries(attrs)) s.setAttribute(k, v)
  document.head.appendChild(s)
}

const LOADERS: Record<string, (id: string) => void> = {
  ga4(id) {
    window.dataLayer = window.dataLayer || []
    // gtag.js only acts on the Arguments object. Pushing an array (as rest
    // params would) is read as a data-layer method call, and every config,
    // page view and event is silently dropped.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments)
    }
    window.gtag('js', new Date())
    // send_page_view is off; SPA navigations are reported by trackPageView.
    window.gtag('config', id, { send_page_view: false })
    inject(`https://www.googletagmanager.com/gtag/js?id=${id}`)
    gaOn = true
  },

  hotjar(id) {
    if (!window.hj) {
      const stub = function hj(...args: unknown[]) {
        stub.q = stub.q || []
        stub.q.push(args)
      } as HotjarStub
      window.hj = stub
    }
    window._hjSettings = { hjid: Number(id), hjsv: 6 }
    inject(`https://static.hotjar.com/c/hotjar-${id}.js?sv=6`)
  },

  metaPixel(id) {
    if (window.fbq) return
    // Meta's own snippet, transcribed: a queueing stub that the real script
    // drains once it loads. `callMethod` only exists after that happens.
    interface Fbq {
      (...args: unknown[]): void
      callMethod?: (...args: unknown[]) => void
      queue: unknown[][]
      push: unknown
      loaded: boolean
      version: string
    }
    const n = function fbq(...args: unknown[]) {
      if (n.callMethod) n.callMethod(...args)
      else n.queue.push(args)
    } as Fbq
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    window.fbq = n
    window._fbq = n
    inject('https://connect.facebook.net/en_US/fbevents.js')
    window.fbq('init', id)
    window.fbq('track', 'PageView')
  },

  hubspot(id) {
    // Keep HubSpot tracking and forms, but never load its chat widget.
    window.hsConversationsSettings = { loadImmediately: false }
    inject(`https://js.hs-scripts.com/${id}.js`, { id: 'hs-script-loader', defer: '' })
  },

  linkedin(id) {
    // The Insight Tag reads its id off the array, not the function call, and
    // queues conversions in `lintrk.q` until insight.min.js drains them.
    window._linkedin_partner_id = id
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || []
    if (!window._linkedin_data_partner_ids.includes(id)) {
      window._linkedin_data_partner_ids.push(id)
    }
    if (!window.lintrk) {
      const stub = function lintrk(...args: unknown[]) {
        stub.q = stub.q || []
        stub.q.push(args)
      } as LintrkStub
      stub.q = []
      window.lintrk = stub
    }
    inject('https://snap.licdn.com/li.lms-analytics/insight.min.js')
  },
}

/**
 * Load the tags the visitor has consented to.
 *
 * Safe to call repeatedly — each tag loads once. When consent is granted later,
 * calling again picks up only the newly-permitted tags.
 */
export function loadAnalytics(consent: Consent | null) {
  for (const tag of TAGS) {
    if (loaded.has(tag.name)) continue
    if (!allows(consent, tag.category)) continue
    try {
      LOADERS[tag.name]?.(tag.id)
      loaded.add(tag.name)
    } catch {
      // A blocked or failed tag must never take the page down with it.
    }
  }
}

/**
 * Report a page view to GA4, and to the Meta Pixel unless `pixel` is false.
 * GA4 is configured with `send_page_view: false`, so every view, the landing
 * page included, is reported here. The Pixel reports its own first PageView
 * when it loads, so the landing view passes `pixel: false`.
 */
export function trackPageView(path: string, title: string, { pixel = true }: { pixel?: boolean } = {}) {
  try {
    if (gaOn) window.gtag?.('event', 'page_view', {
      page_path: path,
      page_title: title,
      page_location: window.location.href,
    })
    if (pixel) window.fbq?.('track', 'PageView')
  } catch {
    /* never break navigation for a tag */
  }
}

/**
 * A visitor declined after tags had already loaded by default: stop reporting
 * now. GA stops sending and drops its cookies' use, the Pixel and HubSpot stop
 * tracking; nothing loads on the next page.
 */
export function revokeAnalytics(consent: Consent) {
  try {
    if (!consent.analytics && loaded.has('ga4')) {
      window.gtag?.('consent', 'update', { analytics_storage: 'denied' })
      gaOn = false
    }
    if (!consent.marketing) {
      window.fbq?.('consent', 'revoke')
      const w = window as Window & { _hsq?: unknown[][] }
      if (loaded.has('hubspot')) (w._hsq = w._hsq || []).push(['doNotTrack'])
    }
  } catch {
    /* never break the page for a tag */
  }
}

/**
 * A named interaction, for the few places a page wants more than a page view:
 * which CTA was clicked and where it sat. Params are controlled values only,
 * never personal data. Silently a no-op until consent has loaded gtag.
 */
export function trackEvent(name: string, params: Record<string, string> = {}) {
  if (gaOn) window.gtag?.('event', name, params)
  if (name === 'cta_click') {
    const metaEvent = trialClickEvent(params.placement)
    if (metaEvent) {
      try {
        window.fbq?.('trackCustom', metaEvent.name, { placement: metaEvent.placement })
      } catch {
        /* never break a click for a tag */
      }
    }
  }
}

/**
 * Studio page CTAs that open the signup (hero, proof, pricing, closing band, header, mobile
 * bar, video popup). Their GA4 placements stay as they are; the Pixel gets `studio_<placement>`.
 */
const STUDIO_TRIAL_PLACEMENTS = new Set([
  'hero',
  'proof',
  'pricing',
  'final',
  'header',
  'bottom-bar',
  'video',
  'video_end',
])

/** The Meta custom event for a signup CTA click on the current page, if it is one the campaigns optimize for. */
function trialClickEvent(placement = ''): { name: string; placement: string } | null {
  if (isJewelerTrialClick(placement)) return { name: 'JewelerTrialClick', placement }
  if (isStudioTrialClick(placement)) return { name: 'StudioTrialClick', placement: `studio_${placement}` }
  return null
}

function isStudioTrialClick(placement = '') {
  const path = window.location.pathname.replace(/\/+$/, '')
  return path === '/studio' && STUDIO_TRIAL_PLACEMENTS.has(placement)
}

/** Jewelers page CTAs that open the Studio signup, the conversion the Meta campaign optimizes for. */
const JEWELER_TRIAL_PLACEMENTS = new Set([
  'jewelers_hero',
  'jewelers_pricing',
  'jewelers_nav',
  'bottom-bar',
  'bottom_cta',
])

function isJewelerTrialClick(placement = '') {
  if (!window.location.pathname.startsWith('/studio-for-jewelers')) return false
  return JEWELER_TRIAL_PLACEMENTS.has(placement) || placement.startsWith('jewelers_starter')
}
